import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import AttendeeImportantInfoButton from "../../Attendees/AttendeeImportantInfoButton";

interface AttendeeSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectAttendee: (attendee: any) => void;
  attendees: any[];
  loading?: boolean;
}

const AttendeeSelectionDialog: React.FC<AttendeeSelectionDialogProps> = ({
  open,
  onClose,
  onSelectAttendee,
  attendees,
  loading = false,
}) => {
  const intl = useIntl();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredAttendees, setFilteredAttendees] = useState(attendees || []);

  useEffect(() => {
    if (!attendees) return;

    if (searchTerm.trim() === "") {
      setFilteredAttendees(attendees);
    } else {
      const filtered = attendees.filter((attendee) =>
        `${attendee.firstName} ${attendee.lastName} ${attendee.email} ${
          attendee.phone || ""
        }`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );
      setFilteredAttendees(filtered);
    }
  }, [searchTerm, attendees]);

  const handleSelectAttendee = (attendee: any) => {
    onSelectAttendee(attendee);
    setSearchTerm("");
    onClose();
  };

  const handleClose = () => {
    setSearchTerm("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage
          id="admin.paymentHistory.selectAttendee"
          defaultMessage="Select Attendee for Manual Payment"
        />
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder={intl.formatMessage({
            id: "admin.paymentHistory.searchAttendees",
            defaultMessage: "Search attendees...",
          })}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxHeight: 400, overflow: "auto" }}>
            {filteredAttendees.length > 0 ? (
              <List>
                {filteredAttendees.map((attendee) => (
                  <ListItem key={attendee.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleSelectAttendee(attendee)}
                      sx={{ flexDirection: "column", alignItems: "stretch" }}
                    >
                      <ListItemText
                        primary={`${attendee.firstName} ${attendee.lastName}`}
                        secondary={
                          <Box>
                            <Box component="div">{attendee.email}</Box>
                            {attendee.phone && (
                              <Box
                                component="div"
                                sx={{
                                  fontSize: "0.75rem",
                                  color: "text.secondary",
                                }}
                              >
                                {attendee.phone}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                      <AttendeeImportantInfoButton attendee={attendee} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ py: 3 }}
              >
                <FormattedMessage
                  id="admin.paymentHistory.noAttendeesFound"
                  defaultMessage="No attendees found"
                />
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          <FormattedMessage id="admin.common.cancel" defaultMessage="Cancel" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttendeeSelectionDialog;
